/**
 * Text content for the furniture info labels drawn on the quotation drawing (SRS REQ-401).
 *
 * A label is the small colored patch painted above/below a furniture highlight carrying the values
 * a reviewer needs to confirm the item without opening the sidebar. The per-type rules mirror the
 * backend result file:
 *
 * | Type      | Line 1                                        | Line 2                     |
 * |-----------|-----------------------------------------------|----------------------------|
 * | Cabinet   | `selected_model_number.model_number`          | `{width} {height} {depth}` |
 * | Countertop| `CT with backsplash ({w} {h} {d})` — one line | —                          |
 * | Other     | `fixed_price_config.name`                     | —                          |
 * | Custom    | `custom_object_config.custom_model_number.model_number` | `{width} {height} {depth}` |
 *
 * Countertops and "Other" objects are the single-line types. A countertop's two fields run along
 * the strip rather than stacking, because it is a long, shallow box with width to spare and no
 * height — see the branch below. An "Other" object is single-line for a different reason: it has
 * only its name to show. Its price USED to be a second line and was removed in #1399 — the amount
 * belongs to the sidebar, where it is editable, not to the drawing.
 *
 * Everything is best-effort: render what exists, omit the rest. An item that yields no lines gets
 * no label drawn at all (there is deliberately no "Model not confirmed" placeholder on the drawing —
 * that copy belongs to the sidebar's `FurnitureModelLabel`).
 */

import {
	isCountertop,
	isCustomFurniture,
	isOtherFurniture,
	isWallCabinet,
} from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import type { TFurniture } from "types/elevationReferences.type"

/** Which side of the base highlight a type's label prefers to sit on. */
export type TFurnitureLabelSide = "above" | "below"

/**
 * Dimensions are inches; drop insignificant decimals so `25.00` reads as `25`. Coerced rather than
 * trusted: these are Django REST `DecimalField`s, which this codebase already treats as
 * possibly-string elsewhere (`TModelNumber.min_size`/`avg_size` are declared `string`), and a
 * string arriving here would throw inside the label sync — which runs synchronously in an effect
 * body and would take the whole editor down.
 */
const formatDimension = (value: number): string | null => {
	const numeric = Number(value)
	return Number.isFinite(numeric) ? String(Math.round(numeric * 100) / 100) : null
}

/**
 * `width height depth`, space-separated, no units and no separators (`25 34 25`). Returns null
 * when ANY of the three is missing or unparseable — a partial triple would be ambiguous, so the
 * whole line is dropped instead.
 */
export const formatFurnitureDimensions = (item: TFurniture): string | null => {
	const { width, height, depth } = item
	if (width == null || height == null || depth == null) return null
	const parts = [formatDimension(width), formatDimension(height), formatDimension(depth)]
	if (parts.some((part) => part === null)) return null
	return parts.join(" ")
}

/**
 * Countertops and wall cabinets sit ABOVE their box; everything else below. Both are drawn along
 * the top of an elevation — the space beneath them is taken by the countertop run and the base
 * cabinets — so a label hung underneath would land on the neighbours instead of on free drawing.
 */
export const getFurnitureLabelSide = (item: TFurniture): TFurnitureLabelSide =>
	isCountertop(item) || isWallCabinet(item) ? "above" : "below"

/**
 * On-drawing shorthand for a countertop's category name.
 *
 * The backend taxonomy ships exactly two countertop leaves — `"Standalone Countertop"` and
 * `"Countertop with a backsplash"` (`Countertops › General Countertops`, per `GET /categories`).
 * Both are far too long for a countertop run, which is a shallow box: shrink-to-fit drops them
 * straight to `minFontSize` and they still overflow. `CT` is the shorthand the estimators use on
 * paper, so the drawing uses it too — `Standalone CT` and `CT with backsplash`, so the two leaves
 * stay distinguishable on the sheet rather than both collapsing to a bare `CT`.
 *
 * Matching is on the presence of "backsplash" / "standalone" / "countertop" rather than the exact
 * leaf names, so a renamed or newly added countertop leaf still abbreviates. A countertop that is
 * neither standalone nor backsplashed keeps the bare `CT`. Anything that mentions none of the three
 * is passed through untouched.
 */
export const abbreviateCountertopName = (name: string): string => {
	if (/backsplash/i.test(name)) return "CT with backsplash"
	if (/standalone/i.test(name)) return "Standalone CT"
	if (/countertop/i.test(name)) return "CT"
	return name
}

/**
 * The 0–2 text lines for an item's on-drawing label, in draw order. An empty array means "draw no
 * label for this object".
 */
export const buildFurnitureLabelLines = (item: TFurniture): string[] => {
	const dimensions = formatFurnitureDimensions(item)

	// #1346 — a custom object labels exactly like a cabinet (model number over dimensions), but its
	// number comes from its linked definition rather than from `selected_model_number`, which is
	// always null for one. Tested before the Other branch: a custom object has no category either, so
	// order is what keeps the two apart.
	if (isCustomFurniture(item)) {
		const lines: string[] = []
		const modelNumber = item.custom_object_config?.custom_model_number?.model_number?.trim()
		if (modelNumber) lines.push(modelNumber)
		if (dimensions) lines.push(dimensions)
		return lines
	}

	if (isOtherFurniture(item)) {
		// Name and nothing else (#1399). The price was the second line until then; it is deliberately
		// NOT replaced by the dimensions triple every other type carries — the issue asked for a
		// removal, and an "Other" object's dimensions are not what identifies it on the sheet.
		//
		// So an unnamed "Other" now yields zero lines and gets no patch drawn, where before it drew a
		// bare `$12.00`. That is the intended outcome of the same rule that governs every other type:
		// render what exists, and draw nothing at all for an item that yields no lines.
		const name = item.fixed_price_config?.name?.trim()
		return name ? [name] : []
	}

	if (isCustomFurniture(item)) {
		// Same shape as the cabinet fallback below, reading the model number off
		// `custom_object_config` instead of `selected_model_number` — a custom object's `selected_
		// model_number` is always null, so without this branch it would fall through to the cabinet
		// case and draw only the dimensions line, with no name at all.
		const lines: string[] = []
		const modelNumber = item.custom_object_config?.custom_model_number?.model_number?.trim()
		if (modelNumber) lines.push(modelNumber)
		if (dimensions) lines.push(dimensions)
		return lines
	}

	if (isCountertop(item)) {
		// ONE line — `CT with backsplash (96 4 25)` — unlike every other type, which stacks two.
		//
		// A countertop run is a long, shallow strip: it has width to spare and almost no height. A
		// two-line patch is nearly twice as tall as a one-line one, which is what used to make these
		// labels clip the wall cabinets overhead and get pushed away from their own object. Laying the
		// two fields out along the strip's long axis instead matches the shape being labelled.
		//
		// The brackets are what let the fields share a line without reading as two unrelated facts —
		// they tie the measurements to the type name. A cabinet needs no such marker: its first field
		// is a model number, so the bare triple beneath it is already unambiguous.
		//
		// Inline unconditionally, not just when the object is wide enough: the font shrinks to fit, so
		// a narrow countertop gets smaller text rather than a different layout. Switching format on
		// width would make otherwise-identical countertops look different on the same sheet.
		const name = item.category?.name?.trim()
		const parts: string[] = []
		if (name) parts.push(abbreviateCountertopName(name))
		if (dimensions) parts.push(`(${dimensions})`)
		return parts.length > 0 ? [parts.join(" ")] : []
	}

	// Typical cabinet: model number over dimensions. Either half may be missing.
	const lines: string[] = []
	const modelNumber = item.selected_model_number?.model_number?.trim()
	if (modelNumber) lines.push(modelNumber)
	if (dimensions) lines.push(dimensions)
	return lines
}
